import { useRef, useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Pencil, Eraser, Trash2, Download, Palette, Send } from 'lucide-react';
import { motion } from 'motion/react';
import type { ActivitySlideProps } from '../types';

// Helper function to strip HTML tags (used for aria-label only)
function stripHtml(html: string): string {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
}

const RICH_TEXT_STYLES = `
  .rich-text-content h1 {
    font-size: 2em;
    font-weight: bold;
    margin-bottom: 0.5em;
  }
  .rich-text-content h2 {
    font-size: 1.5em;
    font-weight: bold;
    margin-bottom: 0.5em;
  }
  .rich-text-content h3 {
    font-size: 1.25em;
    font-weight: bold;
    margin-bottom: 0.5em;
  }
  .rich-text-content strong {
    font-weight: bold;
  }
  .rich-text-content em {
    font-style: italic;
  }
  .rich-text-content u {
    text-decoration: underline;
  }
  .rich-text-content ul {
    list-style-type: disc;
    padding-left: 2em;
    margin-bottom: 1em;
  }
  .rich-text-content ol {
    list-style-type: decimal;
    padding-left: 2em;
    margin-bottom: 1em;
  }
  .rich-text-content li {
    margin-bottom: 0.5em;
  }
  .rich-text-content p {
    margin-bottom: 0.5em;
  }
  .rich-text-content .ql-align-center {
    text-align: center;
  }
  .rich-text-content .ql-align-right {
    text-align: right;
  }
  .rich-text-content .ql-align-left {
    text-align: left;
  }
  .rich-text-content .ql-align-justify {
    text-align: justify;
  }
`;

type Tool = 'pen' | 'eraser';

export default function DrawIt({
  slide,
  onSubmit,
  onAnswerChange,
  hasSubmitted,
  className = '',
}: ActivitySlideProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState<Tool>('pen');
  const [color, setColor] = useState('#6366F1');
  const [lineWidth, setLineWidth] = useState(3);
  const [ctx, setCtx] = useState<CanvasRenderingContext2D | null>(null);
  const [hasDrawn, setHasDrawn] = useState(false);

  const { content } = slide;
  const instructionsHtml = content?.instructions || '';
  const instructionsPlain = stripHtml(instructionsHtml) || 'Draw your answer';
  const bgColor = content?.backgroundColor || '#ffffff';
  const bgImageUrl = content?.backgroundImageUrl;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    context.lineCap = 'round';
    context.lineJoin = 'round';
    setCtx(context);

    // Set background
    context.fillStyle = bgColor;
    context.fillRect(0, 0, canvas.width, canvas.height);

    // Load background image if provided
    if (bgImageUrl) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        context.drawImage(img, 0, 0, canvas.width, canvas.height);
      };
      img.src = bgImageUrl;
    }
  }, [bgColor, bgImageUrl]);

  const getCanvasCoords = (clientX: number, clientY: number) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect || !canvasRef.current) return null;
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!ctx || hasSubmitted) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    canvasRef.current?.setPointerCapture(e.pointerId);
    setIsDrawing(true);
    const coords = getCanvasCoords(e.clientX, e.clientY);
    if (coords) {
      ctx.beginPath();
      ctx.moveTo(coords.x, coords.y);
    }
  };

  const draw = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !ctx || hasSubmitted) return;
    e.preventDefault();

    const coords = getCanvasCoords(e.clientX, e.clientY);
    if (coords) {
      ctx.strokeStyle = tool === 'eraser' ? bgColor : color;
      ctx.lineWidth = tool === 'eraser' ? lineWidth * 3 : lineWidth;
      ctx.lineTo(coords.x, coords.y);
      ctx.stroke();
      setHasDrawn(true);
    }
  };

  const stopDrawing = (e?: React.PointerEvent<HTMLCanvasElement>) => {
    if (e) {
      e.preventDefault();
      if (canvasRef.current?.hasPointerCapture(e.pointerId)) {
        canvasRef.current.releasePointerCapture(e.pointerId);
      }
    }
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    if (!ctx || !canvasRef.current || hasSubmitted) return;
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);

    if (bgImageUrl) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        ctx.drawImage(img, 0, 0, canvasRef.current!.width, canvasRef.current!.height);
      };
      img.src = bgImageUrl;
    }

    setHasDrawn(false);
  };

  const downloadCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = 'drawing.png';
    link.href = canvas.toDataURL();
    link.click();
  };

  const handleSubmitDrawing = () => {
    const canvas = canvasRef.current;
    if (!canvas || hasSubmitted) return;
    const dataURL = canvas.toDataURL('image/png');
    onAnswerChange({ drawing: dataURL });
    setTimeout(() => onSubmit({ drawing: dataURL }), 0);
  };

  const colors = [
    '#6366F1', '#10B981', '#F59E0B', '#EF4444',
    '#8B5CF6', '#EC4899', '#14B8A6', '#000000',
  ];

  return (
    <div className={`w-full ${className}`}>
      <style>{RICH_TEXT_STYLES}</style>
      <div className="bg-gradient-to-br from-pink-500/5 via-purple-500/5 to-blue-500/5 rounded-3xl p-5 sm:p-6 border border-border/40">
        {/* Header */}
        <div className="text-center mb-4">
          <div className="inline-flex items-center gap-2 bg-pink-500/10 text-pink-600 px-4 py-1.5 rounded-full mb-2">
            <Palette className="w-4 h-4" />
            <span className="text-sm font-bold">Draw It!</span>
          </div>
          <div className="bg-card/60 backdrop-blur-sm rounded-xl p-3 border border-border/30 max-w-lg mx-auto">
            {instructionsHtml ? (
              <div className="font-medium text-sm rich-text-content" dangerouslySetInnerHTML={{ __html: instructionsHtml }} />
            ) : (
              <p className="font-medium text-sm">{instructionsPlain}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Toolbar */}
          <div className="lg:col-span-1 space-y-3">
            <div className="bg-card/60 backdrop-blur-sm rounded-2xl p-4 border border-border/40">
              <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-3">Tools</h3>
              <div className="grid grid-cols-2 gap-2">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setTool('pen')}
                  disabled={hasSubmitted}
                  className={`flex items-center justify-center gap-1.5 p-2.5 rounded-xl text-sm font-medium transition-all ${
                    tool === 'pen'
                      ? 'bg-primary text-white shadow-md shadow-primary/20'
                      : 'bg-muted/50 text-foreground hover:bg-muted'
                  } ${hasSubmitted ? 'opacity-50' : ''}`}
                >
                  <Pencil className="w-4 h-4" />
                  Pen
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setTool('eraser')}
                  disabled={hasSubmitted}
                  className={`flex items-center justify-center gap-1.5 p-2.5 rounded-xl text-sm font-medium transition-all ${
                    tool === 'eraser'
                      ? 'bg-orange-500 text-white shadow-md shadow-orange-500/20'
                      : 'bg-muted/50 text-foreground hover:bg-muted'
                  } ${hasSubmitted ? 'opacity-50' : ''}`}
                >
                  <Eraser className="w-4 h-4" />
                  Eraser
                </motion.button>
              </div>
            </div>

            <div className="bg-card/60 backdrop-blur-sm rounded-2xl p-4 border border-border/40">
              <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-3">Colors</h3>
              <div className="grid grid-cols-4 gap-2">
                {colors.map((c) => (
                  <motion.button
                    key={c}
                    whileHover={{ scale: 1.15 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => !hasSubmitted && setColor(c)}
                    disabled={hasSubmitted}
                    className={`w-9 h-9 rounded-xl transition-all ${
                      color === c ? 'ring-3 ring-primary ring-offset-2 scale-110' : ''
                    } ${hasSubmitted ? 'opacity-50' : ''}`}
                    style={{ backgroundColor: c }}
                    aria-label={`Select color ${c}`}
                    aria-pressed={color === c}
                  />
                ))}
              </div>
            </div>

            <div className="bg-card/60 backdrop-blur-sm rounded-2xl p-4 border border-border/40">
              <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-3" id="brush-size-label">Brush Size</h3>
              <input
                type="range"
                min="1"
                max="20"
                value={lineWidth}
                onChange={(e) => setLineWidth(Number(e.target.value))}
                className="w-full accent-primary"
                disabled={hasSubmitted}
                aria-labelledby="brush-size-label"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>Thin</span>
                <span className="font-medium">{lineWidth}px</span>
                <span>Thick</span>
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 rounded-xl border-border/40" size="sm" onClick={clearCanvas} disabled={hasSubmitted}>
                <Trash2 className="w-4 h-4 mr-1" />
                Clear
              </Button>
              <Button variant="outline" className="flex-1 rounded-xl border-border/40" size="sm" onClick={downloadCanvas}>
                <Download className="w-4 h-4 mr-1" />
                Save
              </Button>
            </div>
          </div>

          {/* Canvas */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-lg border-2 border-border/30 overflow-hidden">
              <canvas
                ref={canvasRef}
                width={800}
                height={600}
                onPointerDown={startDrawing}
                onPointerMove={draw}
                onPointerUp={stopDrawing}
                onPointerLeave={stopDrawing}
                onPointerCancel={stopDrawing}
                className={`w-full ${hasSubmitted ? 'cursor-not-allowed opacity-70' : 'cursor-crosshair'}`}
                style={{ touchAction: 'none' }}
                aria-label={`Drawing canvas: ${instructionsPlain}`}
                role="img"
              />
            </div>

            <div className="mt-4 text-center">
              {hasSubmitted ? (
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="inline-flex items-center gap-2 bg-accent/10 text-accent px-5 py-2.5 rounded-full font-bold"
                >
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: 'spring' }}>
                    ✅
                  </motion.div>
                  Drawing submitted!
                </motion.div>
              ) : (
                <Button
                  size="lg"
                  className="rounded-2xl px-8 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white shadow-lg"
                  onClick={handleSubmitDrawing}
                  disabled={!hasDrawn}
                >
                  <Send className="w-4 h-4 mr-2" />
                  Submit Drawing
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
